import { useCallback, useEffect, useState } from 'react';
import { t } from '@lingui/macro';

import { DataLayerEvent } from '@rover/types';
import fireDataLayerEvent from '@rover/utilities/fireDataLayerEvent';

import {
  NotificationsAnnouncements,
  useApiAnnouncementsNotificationsDetailPartialUpdate,
  useApiAnnouncementsNotificationsList,
} from '../../../apiClient/latest';

import { useCurrentUser } from './CurrentUser';

export type UseNotificationsCenter = {
  notifications: NotificationsAnnouncements[] | undefined;
  isLoading: boolean;
  unreadCountDisplay: string;
  reinitialize: () => Promise<void>;
  fireNotificationCenterOpened: () => Promise<void>;
  onMessageClick: (opk: string) => Promise<void>;
  onMessageDismiss: (opk: string) => Promise<void>;
  onMessageRead: (opk: string) => Promise<void>;
  onRequestLoadMoreNotifications: () => Promise<void>;
  setNotificationsCenterHasOpened: () => void;
};

enum NotificationEvent {
  VIEWED = 'notification-viewed',
  CLICKED = 'notification-clicked',
  DISMISSED = 'notification-dismissed',
  OPENED = 'notification-center-open',
}

const toEvent = (
  event: NotificationEvent,
  notification: NotificationsAnnouncements | null | undefined
): DataLayerEvent => ({
  event,
  ...(notification && {
    notificationOpk: notification.opk,
    ...(event === NotificationEvent.CLICKED && {
      notificationSlug: notification.slug,
    }),
  }),
});

const mergeNotifications = (
  a: NotificationsAnnouncements[] = [],
  b: NotificationsAnnouncements[] = []
): NotificationsAnnouncements[] =>
  [...a, ...b].reduce((acc, obj) => {
    if (!acc.some((item) => item.opk === obj.opk)) acc.push(obj);
    return acc;
  }, [] as NotificationsAnnouncements[]);

const useNotificationsCenter = ({
  enabled = true,
}: Partial<{ enabled: boolean }> = {}): UseNotificationsCenter => {
  const [page, setPage] = useState(1);

  const { data, isLoading, refetch } = useApiAnnouncementsNotificationsList(
    { page },
    { query: { enabled } }
  );

  const { mutate } = useApiAnnouncementsNotificationsDetailPartialUpdate();

  const [notifications, setNotifications] = useState<NotificationsAnnouncements[] | undefined>();
  const [clickedNotificationOpks, setClickedNotificationOpks] = useState([] as string[]);
  const [hasOpened, setHasOpened] = useState(false);
  const [unreadCountDisplay, setUnreadCountDisplay] = useState('');
  const { isAuthenticated } = useCurrentUser({ enabled });

  useEffect(() => {
    if (!notifications && !data) return;
    setNotifications(mergeNotifications(notifications, data?.results));
  }, [data?.results]);

  useEffect(() => {
    const unreadCount = data?.unreadCount;
    if (!unreadCount || unreadCount <= 0 || hasOpened) setUnreadCountDisplay('');
    else if (unreadCount >= 1 && unreadCount <= 10) setUnreadCountDisplay(unreadCount.toString());
    else setUnreadCountDisplay(t`10+`);
  }, [notifications, hasOpened]);

  useEffect(() => {
    // Remove clicked row IDs that are not in the updated list
    setClickedNotificationOpks((prevClickedNotificationOpks) =>
      prevClickedNotificationOpks.filter((opk) => notifications?.some((n) => n.opk === opk))
    );
  }, [notifications]);

  const getNotification = useCallback(
    (opk: string): NotificationsAnnouncements | undefined => {
      return notifications?.find((n) => n.opk === opk);
    },
    [notifications]
  );

  const removeNotification = useCallback(
    (opk: string) => {
      setNotifications((prev) => prev?.filter((n) => n.opk !== opk));
    },
    [notifications]
  );

  const onMessageDismiss = useCallback(
    async (opk: string) => {
      const notification = getNotification(opk);
      const dataLayerPromise = fireDataLayerEvent(
        toEvent(NotificationEvent.DISMISSED, notification)
      );

      if (notification) {
        mutate({ opk, data: { isDismissed: true } });
        removeNotification(opk);
      }

      await dataLayerPromise;
    },
    [mutate, getNotification]
  );

  const setNotificationRead = useCallback(
    (opk: string) => {
      setNotifications((prev) => prev?.map((n) => (n.opk === opk ? { ...n, read: true } : n)));
    },
    [notifications]
  );

  const onMessageRead = useCallback(
    async (opk: string) => {
      const notification = getNotification(opk);
      const dataLayerPromise = fireDataLayerEvent(toEvent(NotificationEvent.VIEWED, notification));

      if (notification && !notification.read) {
        setNotificationRead(opk);
        mutate({ opk, data: { read: true } });
      }

      await dataLayerPromise;
    },
    [data, mutate, getNotification]
  );

  const recordNotificationClick = useCallback((clickedOpk: string): void => {
    setClickedNotificationOpks((prevClickedNotificationOpk) => {
      if (prevClickedNotificationOpk.includes(clickedOpk)) {
        return prevClickedNotificationOpk.filter((o) => o !== clickedOpk);
      }
      return [...prevClickedNotificationOpk, clickedOpk];
    });
  }, []);

  const onMessageClick = useCallback(
    async (opk) => {
      const notification = getNotification(opk);
      if (!notification || clickedNotificationOpks.find((o) => o === opk)) return;
      const dataLayerPromise = fireDataLayerEvent(toEvent(NotificationEvent.CLICKED, notification));
      recordNotificationClick(opk);
      await dataLayerPromise;
      if (notification.webAction) window.location.href = notification.webAction;
    },
    [getNotification]
  );

  const fireNotificationCenterOpened = useCallback(async () => {
    await fireDataLayerEvent(toEvent(NotificationEvent.OPENED, undefined));
  }, []);

  const onRequestLoadMoreNotifications = useCallback(async () => {
    if (!data?.next) return;
    setPage(page + 1);
  }, [data]);

  const refetchCallback = useCallback(async () => {
    if (isAuthenticated) await refetch();
  }, [isAuthenticated]);

  return {
    notifications,
    isLoading,
    unreadCountDisplay,
    reinitialize: refetchCallback,
    onMessageDismiss,
    onMessageRead,
    onMessageClick,
    fireNotificationCenterOpened,
    onRequestLoadMoreNotifications,
    setNotificationsCenterHasOpened: () => setHasOpened(true),
  };
};

export default useNotificationsCenter;
